import { Grid, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, isAfter, parseISO } from "date-fns";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import FormTable from "../../../common/components/functional/formTable/FormTable";
import { Talent, talentIsDeactivated } from "../../talents/talentAPI";
import {
  removeTalentTraining,
  selectTalentById,
} from "../../talents/talentsSlice";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { ContentKey } from "../TalentDetails";

type Props = {
  handleOpenEditModal: (content: ContentKey, id?: string) => void;
};

const TalentTrainings = ({ handleOpenEditModal }: Props) => {
  const { talentId } = useParams();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );
  const dispatch = useAppDispatch();
  const trainings = talent.trainings;
  const { canEditTalentProfile } = useContext(AccessRightContext);

  const translatedTrainings = useAppSelector(
    (state) => state.localized.training,
  );

  const { t } = useTranslation("trainings");

  const handleDelete = (id: string) => {
    const deletableTraining = trainings?.find((training) => training.id === id);

    if (deletableTraining) {
      const commandPayload = {
        id: deletableTraining.id,
        version: deletableTraining.version,
      };
      dispatch(removeTalentTraining(commandPayload)).then((action) =>
        isFulfilled(action),
      );
    }
  };

  // TODO: check if this works
  const checkIsExpired = (validTo?: string) => {
    const today = parseISO(format(new Date(), "yyyy-MM-dd"));
    const isExpired = validTo && isAfter(today, parseISO(validTo));
    return isExpired;
  };

  const getCompleted = (
    completed: boolean,
    completedAt?: string,
    validTo?: string,
  ) => {
    if (completed) {
      if (completedAt) {
        const parsedDate = parseISO(completedAt);
        const formattedDate = format(parsedDate, "dd.MM.yyyy");
        const formattedCompletedAt = completedAt
          ? `${t("completed")} ${formattedDate}`
          : t("completed");
        return <Typography component="span">{formattedCompletedAt}</Typography>;
      } else {
        // for migration cases where completedAt is not set
        return <Typography component="span">{t("completed")}</Typography>;
      }
    }

    const isExpired = checkIsExpired(validTo);

    if (isExpired)
      return (
        <Typography component="span" color="warning.main">
          {t("expired")}
        </Typography>
      );

    return <Typography component="span">{t("trainingInProgress")}</Typography>;
  };

  // TODO: check isExpired
  const getValidTo = (validTo?: string) => {
    const isExpired = checkIsExpired(validTo);

    if (validTo) {
      const parsedDate = parseISO(validTo);
      const formattedDate = format(parsedDate, "dd.MM.yyyy");
      return (
        <Typography
          component="span"
          {...(isExpired && { color: "warning.main" })}
        >
          {formattedDate}
        </Typography>
      );
    }

    return (
      <Typography
        component="span"
        {...(isExpired && { color: "warning.main" })}
      >
        {validTo}
      </Typography>
    );
  };

  const formattedTrainings = trainings
    ? trainings
        .map(({ id, type, completed, completedAt, validTo }) => ({
          values: {
            type: translatedTrainings[type],
            completedAt: getCompleted(completed, completedAt, validTo),
            validTo: getValidTo(validTo),
          },
          deletable: true,
          id,
        }))
        .sort((a, b) => a.values.type?.localeCompare(b.values.type)) // Sort alphabetically
    : [];

  const headers = [
    { text: t("course") },
    { text: t("completed") },
    { text: t("validLong") },
    { text: "" },
  ];

  return (
    <FormSection title={t("trainings")}>
      <Grid container columnSpacing={2}>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        />
        <Grid item xs={9}>
          <FormTable
            data={formattedTrainings}
            headers={headers}
            handleOpenEditModal={handleOpenEditModal}
            contentKey="trainings"
            addButtonLabel={t("addTraining") || ""}
            handleDelete={(id) => handleDelete(id)}
            disableEditRow={
              !canEditTalentProfile || talentIsDeactivated(talent)
            }
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

export default TalentTrainings;
